<template>
  <Sidebar
    :is-open="isOpen"
    :has-changes="hasUnsavedChanges"
    @on-close="closeSidebar"
  >
    <SidebarContent class="pt-6">
      <form
        ref="form"
        @submit.prevent="taskHandlerSubmit"
      >
        <FormGroup>
          <div class="flex items-center">
            <Checkbox
              v-model="state.isCompleted"
              v-tippy="state.isCompleted ? 'Mark as incomplete' : 'Mark as complete'"
              size="lg"
              class="mr-2"
            />
            <input
              ref="inputTitle"
              v-model="state.title"
              type="text"
              :has-error="v$.title.$error"
              class="text-xl w-full py-2 my-2 bg-transparent pr-5"
              :class="{
                'border border-error rounded-md px-[0.5rem] py-[0.75rem]': v$.title.$error
              }"
              :placeholder="$t('ui.task.taskName')"
            />
            <Icon
              v-tippy="state.isStarred ? 'Unmark as important' : 'Mark as important'"
              type="star"
              :fa-style="state.isStarred ? 'fa-solid' : 'fa-regular'"
              class="mx-2 cursor-pointer text-lg"
              :class="[state.isStarred ? 'text-yellow-500' : 'text-quaternary']"
              @click="state.isStarred = !state.isStarred"
            />
          </div>
          <InputError
            :has-error="v$.title.$error"
            class="ml-10"
          >
            {{ v$.title.$errors[0].$message }}
          </InputError>
        </FormGroup>

        <FormGroup>
          <TextArea
            id="description"
            v-model="state.description"
            :value="state.description"
            :has-error="false"
            :placeholder="$t('ui.common.description')"
          ></TextArea>
          <InputError :has-error="v$.description.$error">{{ v$.description.$errors[0].$message }}</InputError>
        </FormGroup>

        <FormGroup
          v-if="state.linkType == 'agreement'"
          :label="$t('ui.entities.agreement.label')"
        >
          <router-link
            v-if="state.linkType == 'agreement'"
            :to="'/agreement/' + state.linkId"
          >
            <Chip icon="link">{{ state.linkTitle }}</Chip>
          </router-link>
        </FormGroup>

        <FormGroup
          :label="$t('ui.entities.task.assignedTo')"
          label-for="assignee"
          :class="{ hidden: !$can('assign_tasks') }"
        >
          <Select
            v-model="state.assignee"
            mode="single"
            searchable
            label-key="fullName"
            :options="usersList"
            value-key="id"
            :placeholder="$t('ui.task.selectUser')"
          >
            <template #value="{ value }">
              <div class="flex items-center">
                <Avatar
                  :url="value.profilePicUrl"
                  :text="value.initials"
                  size="xs"
                  class="mr-2"
                />
                {{ value.fullName }}
              </div>
            </template>

            <template #option="{ option }">
              <div class="flex items-center">
                <Avatar
                  :url="option.profilePicUrl"
                  :text="option.initials"
                  size="sm"
                  class="mr-2"
                />
                {{ option.fullName }}
              </div>
            </template>
          </Select>

          <InputError :has-error="v$.assignee.$error">{{ v$.assignee.$errors[0].$message }}</InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.common.dueDate')"
          label-for="dueDate"
        >
          <DatePicker
            v-model="state.dueDate"
            show-time
            :has-error="v$.dueDate.$error"
            :close-on-select="false"
            :placeholder="$t('ui.common.dueDate')"
          />
          <InputError :has-error="v$.dueDate.$error">{{ v$.dueDate.$errors[0].$message }}</InputError>
        </FormGroup>
        <FormGroup
          v-if="state.createdBy != sessionUserID"
          :label="$t('ui.entities.task.createdBy')"
          label-for="createdBy"
        >
          <Chip
            v-if="isNewTask"
            :avatar="createdByData[0].profilePicUrl"
            :avatar-text="createdByData[0].initials"
          >
            {{ createdByData[0].fullName || '?' }}
          </Chip>
          <Chip
            v-else
            :avatar="state.createdBy.profilePicUrl"
            :avatar-text="state.createdBy.initials"
          >
            {{ state.createdBy.forename + ' ' + state.createdBy.surname || '?' }}
          </Chip>
        </FormGroup>
      </form>
    </SidebarContent>
    <SidebarFooter>
      <div class="flex justify-end">
        <div
          v-if="!isNewTask"
          class="w-full"
        >
          <Button
            v-if="!state.isDeleted"
            severity="danger"
            :label="$t('ui.task.deleteTask')"
            plain
            @click="deleteTask(true)"
          />
          <Button
            v-else
            severity="secondary"
            :label="$t('ui.task.restoreTask')"
            plain
            @click="deleteTask(false)"
          />
        </div>
        <div class="flex justify-end gap-2">
          <Button
            :label="$t('ui.actions.cancel')"
            severity="secondary"
            outlined
            type="button"
            :disabled="isSubmitting"
            @click="closeSidebar"
          />
          <Button
            :label="isNewTask ? $t('ui.actions.add') : $t('ui.actions.save')"
            :is-loading="isSubmitting"
            @click="taskHandlerSubmit"
          />
        </div>
      </div>
    </SidebarFooter>
  </Sidebar>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, computed } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useI18n } from 'vue-i18n'
import DatePicker from '@/components/forms/DatePicker.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'
import { alertToast } from '@/utilities/notification'

// components
import Icon from '@/components/icon/Icon.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Sidebar from '@/components/sidebar/Sidebar.vue'
import SidebarContent from '@/components/sidebar/SidebarContent.vue'
import SidebarFooter from '@/components/sidebar/SidebarFooter.vue'
import Select from '@/components/forms/Select.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import TextArea from '@/components/forms/TextArea.vue'
import InputError from '@/components/forms/InputError.vue'
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'

// stores
import { useAuthStore } from '@/stores/AuthStore'
import { useAccessStore } from '@/stores/AccessStore'
import { useTaskStore } from '@/stores/TaskStore.ts'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  task: {
    type: Object,
    default: () => ({})
  }
})
const emit = defineEmits(['updateTaskSubmit', 'closeTaskHandler', 'newTaskSubmit'])
const { t } = useI18n()
const authStore = useAuthStore()
const accessStore = useAccessStore()
const taskStore = useTaskStore()
const sessionUserID = ref(authStore.details.id)

// Instantiate state
const isNewTask = ref(true)
const isSubmitting = ref(false)

const defaultState = {
  id: null,
  title: '',
  assignee: sessionUserID.value,
  dueDate: '',
  tags: [],
  description: null,
  linkType: null,
  linkTitle: null,
  linkId: null,
  isStarred: false,
  isCompleted: false,
  isReminder: false,
  isDeleted: false,
  createdBy: sessionUserID.value
}

// Initialize stateInit from props.task or defaultState
// For new tasks (no id), merge props.task with defaultState to preserve link properties
const stateInit = props.task?.id ? { ...props.task } : { ...defaultState, ...props.task }

// Store the original assignee UserReference before extracting ID (for pre-populating users list)
const originalAssignee =
  stateInit.assignee && typeof stateInit.assignee === 'object' && stateInit.assignee !== null
    ? stateInit.assignee
    : null

// Normalize stateInit: extract assignee ID
if (stateInit.assignee && typeof stateInit.assignee === 'object' && stateInit.assignee !== null) {
  stateInit.assignee = stateInit.assignee.id
}

// Normalize createdBy - extract ID if it's an object
if (stateInit.createdBy && typeof stateInit.createdBy === 'object' && stateInit.createdBy !== null) {
  stateInit.createdBy = stateInit.createdBy.id ?? stateInit.createdBy
}

// Set isNewTask flag
if (props.task?.id) {
  isNewTask.value = false
}

// Create state from normalized stateInit
const state = ref({ ...stateInit })

// Store a deep copy of the initial state for comparison
const stateInitSnapshot = ref(JSON.parse(JSON.stringify(stateInit)))

const hasUnsavedChanges = ref(false)

onMounted(() => {})

watch(
  state,
  newValue => {
    // Compare the current state with the initial state snapshot
    hasUnsavedChanges.value = JSON.stringify(newValue) !== JSON.stringify(stateInitSnapshot.value)
  },
  { deep: true } // Deep watch to handle nested properties inside the state
)

const inputTitle = ref(null)

// Watch for change in isOpen - reset state when sidebar opens
watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      // Update isNewTask flag
      isNewTask.value = !props.task?.id

      // Reset state to initial values when sidebar opens
      // For new tasks (no id), merge props.task with defaultState to preserve link properties
      const resetState = props.task?.id ? { ...props.task } : { ...defaultState, ...props.task }

      // Normalize resetState the same way we normalized stateInit
      if (resetState.assignee && typeof resetState.assignee === 'object' && resetState.assignee !== null) {
        resetState.assignee = resetState.assignee.id
      }
      if (resetState.createdBy && typeof resetState.createdBy === 'object' && resetState.createdBy !== null) {
        resetState.createdBy = resetState.createdBy.id ?? resetState.createdBy
      }

      // Update state and snapshot
      Object.assign(state.value, resetState)
      stateInitSnapshot.value = JSON.parse(JSON.stringify(resetState))
      hasUnsavedChanges.value = false

      nextTick(() => {
        if (isNewTask.value) {
          inputTitle.value?.focus()
        }
      })
    }
  },
  { immediate: true }
)

const createdByData = ref([
  {
    fullName: null,
    profilePicUrl: null,
    initials: null
  }
])

// Users List
// Pre-populate with assignee UserReference if editing an existing task (for better UX while loading)
const initialUsers = []
if (originalAssignee && typeof originalAssignee === 'object' && 'id' in originalAssignee) {
  initialUsers.push({
    id: originalAssignee.id,
    fullName: `${originalAssignee.forename} ${originalAssignee.surname}`,
    profilePicUrl: originalAssignee.profilePicUrl,
    initials: originalAssignee.initials,
    isActive: true // Ensure it shows up in the list
  })
}
const users = ref(initialUsers)
const usersRequest = useApiRequest()

const dealershipIDs = accessStore.accessAllowed.dealerships.map(dealership => dealership.id)
const dealershipIDsString = dealershipIDs.join(',')

try {
  usersRequest
    .send({
      endpoint: '/v1/users',
      params: {
        userType: authStore.details.userType == 1 ? 'all' : 'externalUsers',
        dealerships: dealershipIDsString,
        showDisabled: true
      }
    })
    .then(response => {
      if (response.success) {
        users.value = response.data
        // If we had a temporary assignee user and it's not in the loaded users, add it back
        if (originalAssignee && typeof originalAssignee === 'object' && 'id' in originalAssignee) {
          const assigneeId = originalAssignee.id
          const assigneeExists = response.data.some(user => user.id == assigneeId)
          if (!assigneeExists) {
            users.value.push({
              id: originalAssignee.id,
              fullName: `${originalAssignee.forename} ${originalAssignee.surname}`,
              profilePicUrl: originalAssignee.profilePicUrl,
              initials: originalAssignee.initials,
              isActive: true
            })
          }
        }
        createdByData.value = response.data.filter(user => parseInt(user.id) === parseInt(state.value.createdBy))
      } else {
        alertToast(t('ui.messages.failedToFetch', { item: t('ui.entities.user.label', 2) }), response.message, 'error')
      }
    })
} catch (err) {
  alertToast(t('ui.messages.failedToFetch', { item: t('ui.entities.user.label', 2) }), err.message, 'error')
}

const usersList = computed(() => {
  const allUsers = users.value
  const activeUsers = allUsers.filter(user => user.isActive)
  const activeUserIds = activeUsers.map(user => user.id)
  // Get assignee ID (state.assignee is a number after extraction from UserReference)
  const assigneeId = state.value.assignee
  // if assigned user not in active users, add to the list
  if (assigneeId && !activeUserIds.includes(assigneeId)) {
    const assignedUser = allUsers.find(user => user.id == assigneeId)
    if (assignedUser) {
      activeUsers.push(assignedUser)
    }
  }
  return activeUsers
})

const vuelidateRules = {
  title: { required },
  assignee: { required },
  dueDate: {},
  tags: '',
  description: ''
}
const v$ = useVuelidate(vuelidateRules, state, { $stopPropagation: true })

function closeSidebar() {
  emit('closeTaskHandler')
}

async function taskHandlerSubmit() {
  const isFormCorrect = await v$.value.$validate()

  if (!isFormCorrect) {
    return
  }

  isSubmitting.value = true

  try {
    // state.assignee is already a number (extracted from UserReference during initialization)
    const payload = {
      assignee: state.value.assignee,
      title: state.value.title,
      description: state.value.description,
      tags: state.value.tags,
      dueDate: state.value.dueDate,
      linkType: state.value.linkType,
      linkTitle: state.value.linkTitle,
      linkId: state.value.linkId,
      isReminder: state.value.isReminder,
      isStarred: state.value.isStarred,
      isCompleted: state.value.isCompleted,
      isDeleted: state.value.isDeleted
    }

    if (isNewTask.value) {
      const newTask = await taskStore.createTask(payload)
      if (newTask) {
        emit('newTaskSubmit', newTask)
      }
    } else {
      const updatedTask = await taskStore.updateTask(state.value.id, payload)
      if (updatedTask) {
        emit('updateTaskSubmit', updatedTask)
      }
    }
  } finally {
    isSubmitting.value = false
  }
}

async function deleteTask(shouldDelete) {
  state.value.isDeleted = shouldDelete
  await taskHandlerSubmit()
}
</script>

<style></style>
